import { TeamCard } from './types/card';
import { SetResults } from './types/game-state';

/*
 * set-scoring — the single source of truth for who wins each of the four set
 * points (high, low, jack, game), shared by the server (scoreTeam) and the
 * client (ScoreResultsModal) so the score applied and the score shown can never
 * disagree.
 */

export interface SetPointWinners {
  /** Team holding the highest trump. Undefined only if no trump was collected. */
  high?: string;
  low?: string;
  /** Undefined when the jack of trump was never dealt into play. */
  jack?: string;
  /** Most card points. Undefined on a tie — nobody scores game. */
  game?: string;
}

export function getSetPointWinners(setResults: SetResults): SetPointWinners {
  const entries = Object.entries(setResults);

  // Team ids paired with the value of one of their trump cards, skipping the
  // teams that collected no trump at all.
  const trumpValues = (cardOf: (r: SetResults[string]) => TeamCard | undefined) =>
    entries.flatMap(([teamId, r]) => {
      const card = cardOf(r);
      return card ? [{ teamId, value: card.value }] : [];
    });

  // Trump lives in exactly one collection, so high and low can never tie.
  const high = trumpValues((r) => r.highTrumpCard).sort((a, b) => b.value - a.value)[0]?.teamId;
  const low = trumpValues((r) => r.lowTrumpCard).sort((a, b) => a.value - b.value)[0]?.teamId;

  const jack = entries.find(([, r]) => r.jackTrumpCard)?.[0];

  const bestGameScore = Math.max(...entries.map(([, r]) => r.gameScore));
  const gameLeaders = entries.filter(([, r]) => r.gameScore === bestGameScore);
  const game = gameLeaders.length === 1 ? gameLeaders[0][0] : undefined;

  return { high, low, jack, game };
}

// How many of the four set points a team took. Uncapped — the bidding team
// still only gains its bid, which is the caller's concern.
export function getPointsEarned(teamId: string, setResults: SetResults): number {
  const winners = getSetPointWinners(setResults);
  return (
    (winners.high === teamId ? 1 : 0) +
    (winners.low === teamId ? 1 : 0) +
    (winners.jack === teamId ? 1 : 0) +
    (winners.game === teamId ? 1 : 0)
  );
}
